import React from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import BaseListingRendererWithPlugins from 'nav/expanded/BaseListingRendererWithPlugins';
import {
  ColumnAccessorKey,
  ColumnConfiguration,
  ListingRendererType,
} from 'nav/expanded/ListingRendererConstants';
import {ProcessTaskStatusCounts} from 'processes/dashboard/ProcessTaskStatusCounts';
import {PROCESS_DASHBOARD_TASK_STATUS_COLUMN_WIDTH} from 'processes/ProcessConstants';
import {NO_ACCESS_TEXT} from 'processes/ProcessConstants';
import * as ExpandedTableTagUtil from 'schemas/tags/ExpandedTableTagUtil';
import {SortKey} from 'search/SearchConstants';
import {getColumnHeaderFromSortKey} from 'search/SortUtil';
import {
  getStudiesRowContentHeight,
  maybeGetStudiesListingColumn,
  StudyableItemListingExtras,
} from 'studies/studyable/StudiesListingColumn';
import {AvatarShape, AvatarSize} from 'ui-library/avatar/AvatarConstants';
import {AvatarWithName} from 'ui-library/avatar/AvatarWithName';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import PartyList from 'ui-library/PartyList';
import {DatetimeTableCell} from 'ui-library/scrollable/table-cells/DatetimeTableCell';
import {BasicEvent} from 'util/Types';

import {RequestV2SubmissionListingGraphQL} from '../../../../graphql-fragments/processes/requests-v2/__generated/RequestV2SubmissionListingGraphQL';
import RequestV2AssigneeDisplay from '../RequestV2AssigneeDisplay';
import RequestV2DefinitionListingDisplay from '../RequestV2DefinitionListingDisplay';
import RequestV2StatusDisplay from '../RequestV2StatusDisplay';

export interface RequestV2SubmissionListingExtras extends StudyableItemListingExtras {}

export default class RequestV2SubmissionListingRenderer extends BaseListingRendererWithPlugins<ListingRendererType.REQUEST_V2_SUBMISSION> {
  listingRendererType = ListingRendererType.REQUEST_V2_SUBMISSION;

  renderIcon(): JSX.Element {
    return (
      <BenchlingIcon icon={ModelTypeConstants.REQUEST_V2_SUBMISSION} className="scrollableTable-cell-icon" />
    );
  }

  getNonPluginColumnConfigurations(
    _: Array<RequestV2SubmissionListingGraphQL>,
    {itemStudyAttributes}: RequestV2SubmissionListingExtras = {}
  ): Array<ColumnConfiguration<RequestV2SubmissionListingGraphQL>> {
    const maybeStudiesColumn =
      maybeGetStudiesListingColumn<RequestV2SubmissionListingGraphQL>(itemStudyAttributes);

    const columns: Array<ColumnConfiguration<RequestV2SubmissionListingGraphQL>> = [
      {
        column: {
          accessorKey: ColumnAccessorKey.NAME,
          header: 'Name',
          isSortable: true,
          sortKey: SortKey.SORT_NAME_KEY,
          flexGrow: 1,
        },
        render: (item) => item.name,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.ID,
          header: 'ID',
          isSortable: true,
          sortKey: SortKey.SORT_IDENTIFIER_KEY,
          flexGrow: 1,
        },
        render: (item) => item.displayId,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.STATUS,
          header: 'Request status',
          isSortable: true,
          sortKey: SortKey.SORT_REQUEST_V2_SUBMISSION_STATUS,
          flexGrow: 1,
        },
        render: (item) => <RequestV2StatusDisplay status={item.status} />,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.TASK_STATUS,
          header: 'Task statuses',
          width: PROCESS_DASHBOARD_TASK_STATUS_COLUMN_WIDTH,
        },
        render: (item) => {
          if (item.workflowTaskGroup == null) {
            return null;
          } else if (isInaccessible(item.workflowTaskGroup)) {
            return NO_ACCESS_TEXT;
          }
          return <ProcessTaskStatusCounts process={item.workflowTaskGroup} />;
        },
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.CREATOR,
          header: 'Creator',
          isSortable: true,
          sortKey: SortKey.SORT_CREATOR_NAME,
          flexGrow: 1,
        },
        render: (item) => (
          <AvatarWithName
            avatarInfo={item.creator}
            shape={AvatarShape.CIRCLE}
            size={AvatarSize.SMALL}
            showTooltip={false}
          />
        ),
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.TYPE,
          header: 'Request type',
          isSortable: true,
          sortKey: SortKey.SORT_REQUEST_V2_DEFINITION_NAME,
          flexGrow: 1,
        },
        render: (item) => <RequestV2DefinitionListingDisplay definitionInfo={item.definition} />,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.ASSIGNEE,
          header: 'Assignees',
          isSortable: false,
          flexGrow: 1,
        },
        render: (item) => <RequestV2AssigneeDisplay assigneeInfo={item.assignees} />,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.WATCHERS,
          header: 'Watchers',
          isSortable: false,
          flexGrow: 1,
        },
        render: (item) => (
          <PartyList
            parties={item.watcherParties}
            maxParties={1}
            totalNumberOfParties={item.watcherParties.length}
            useAvatar={true}
            popoverOverride={<>{item.watcherParties.map((watcher) => watcher.name).join(', ')}</>}
          />
        ),
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.CREATED_AT,
          header: getColumnHeaderFromSortKey(SortKey.SORT_CREATED_AT_KEY),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_CREATED_AT_KEY,
        },
        render: (item) => <DatetimeTableCell datetime={item.createdAt} showTimeInCell={false} />,
      },
      ...(maybeStudiesColumn ? [maybeStudiesColumn] : []),
      {
        column: {
          accessorKey: ColumnAccessorKey.MODIFIED_AT,
          header: getColumnHeaderFromSortKey(SortKey.SORT_MODIFIED_AT_KEY),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_MODIFIED_AT_KEY,
        },
        render: (item) => <DatetimeTableCell datetime={item.modifiedAt} showTimeInCell={false} />,
      },
    ];

    return columns;
  }

  getNonPluginRowContentHeight(
    item: RequestV2SubmissionListingGraphQL,
    {itemStudyAttributes}: RequestV2SubmissionListingExtras
  ): number {
    return Math.max(
      ExpandedTableTagUtil.getRowHeight([]),
      getStudiesRowContentHeight(item.id, itemStudyAttributes)
    );
  }

  openContextMenu(item: RequestV2SubmissionListingGraphQL, e: BasicEvent, extras: unknown): void {
    // TODO: Implement
  }
}
